/**
 * Helper function to format job data into tree data structure with path strings
 */

interface JobWithInstances {
  jobId: number | string;
  children?: JobWithInstances[];
  [key: string]: any;
}

interface TreeDataJob extends JobWithInstances {
  path: string;
}

/**
 * Recursively processes job data to create tree structure with path strings
 * @param jobs - Array of job objects
 * @param parentPath - Parent path string (empty for root level)
 * @returns Flattened array of jobs with path property
 */
export const formatJobsToTreeData = (
  jobs: JobWithInstances[],
  parentPath = ''
): TreeDataJob[] => {
  const result: TreeDataJob[] = [];

  jobs.forEach((job) => {
    // Create path for current job
    const currentPath = parentPath
      ? `${parentPath}/${job.jobId}`
      : `${job.jobId}`;

    // Add current job with path
    const jobWithPath: TreeDataJob = {
      ...job,
      path: currentPath,
    };

    // Add current job to result
    result.push(jobWithPath);

    if (!!job?.children?.length) {
      const childJobs = formatJobsToTreeData(job.children, currentPath);
      result.push(...childJobs);
    }
  });

  return result;
};

/**
 * Alternative version if you need to group jobs by parent-child relationships
 * This assumes you have a parentJobId field to establish relationships
 */
interface JobWithParent {
  jobId: number | string;
  parentJobId?: number | string;
  [key: string]: any;
}

export const formatFlatJobsToTreeData = (
  jobs: JobWithParent[]
): TreeDataJob[] => {
  const result: TreeDataJob[] = [];
  const jobMap = new Map<string | number, JobWithParent>();
  const processedJobs = new Set<string | number>();

  // Create a map for quick lookup
  jobs.forEach((job) => {
    jobMap.set(job.jobId, job);
  });

  // Recursive function to build path for a job
  const buildJobPath = (
    jobId: string | number,
    visited: Set<string | number> = new Set()
  ): string => {
    // Prevent infinite loops
    if (visited.has(jobId)) {
      return `${jobId}`;
    }

    visited.add(jobId);
    const job = jobMap.get(jobId);

    if (!job || !job.parentJobId) {
      return `${jobId}`;
    }

    const parentPath = buildJobPath(job.parentJobId, visited);
    return `${parentPath}/${jobId}`;
  };

  // Process all jobs and add path
  jobs.forEach((job) => {
    if (!processedJobs.has(job.jobId)) {
      const path = buildJobPath(job.jobId);

      result.push({
        ...job,
        path,
      });

      processedJobs.add(job.jobId);
    }
  });

  return result;
};

/**
 * Example usage for your current data structure:
 */
export const formatCurrentJobsData = (jobs: any[]): TreeDataJob[] => {
  // Since your current data is flat and doesn't have instances,
  // we'll just add the path property as the jobId
  return jobs.map((job) => ({
    ...job,
    path: `${job.jobId}`,
  }));
};

/**
 * Mock function to simulate nested data structure for testing
 */
export const createMockNestedData = (jobs: any[]): JobWithInstances[] => {
  // This is just for testing - creates some mock nested structure
  return jobs.map((job, index) => {
    const mockJob: JobWithInstances = { ...job };

    // Add some mock children for every 3rd job
    if (index % 3 === 0 && index < jobs.length - 1) {
      mockJob.children = [
        {
          jobId: `${job.jobId}-child-1`,
          workflow: job.workflow,
          status: 'CHILD_JOB',
          children: [
            {
              jobId: `${job.jobId}-grandchild-1`,
              workflow: job.workflow,
              status: 'GRANDCHILD_JOB',
            },
          ],
        },
        {
          jobId: `${job.jobId}-child-2`,
          workflow: job.workflow,
          status: 'CHILD_JOB',
        },
      ];
    }

    return mockJob;
  });
};
